import { lazy, Suspense, useEffect } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'

import { Cog8ToothIcon } from '@heroicons/react/24/outline'

import useServer from '@/api/useServerMain'
import { Loading } from '@/components'
import Breadcrumb from '@/components/navigation/Breadcrumb'
import AppContainer from '@/containers/AppContainer'
import AreaList from '@/containers/areas/AreaList'
import CategoryList from '@/containers/logistic/categories/CategoryList'
import CompanyList from '@/containers/logistic/companies/CompanyList'
import ContainerList from '@/containers/logistic/containers/ContainerList'
import InventoryList from '@/containers/logistic/inventory/InventoryList'
import { ProductDetailsProvider } from '@/containers/logistic/inventory/contexts/ProductDetailsContext'
import { LogisticInventoryProvider } from '@/containers/logistic/inventory/hooks/useLogisticInventory'
import PurchaseOrderList from '@/containers/logistic/purchaseOrders/PurchaseOrderList'
import ShipmentNotificationList from '@/containers/logistic/shipmentNotifications/ShipmentNotificationList'
import VehicleList from '@/containers/logistic/vehicles/VehicleList'
import LogisticWrapper from '@/containers/logistic/components/LogisticWrapper'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useBusinessPackagesQuery } from '@/hooks/business/useBusinessPackagesQuery'
import { HomePage } from '@/pages'
import NotFoundPage from '@/pages/NotFoundPage'
import { useAppSelector } from '@/store/hooks'

//Areas

const DetailAreaContainer = lazy(
  () => import('@/containers/areas/DetailAreaContainer')
)

//Shared Areas
const ListShareAreas = lazy(
  () => import('@/containers/config/GeneralAdjustment/shareArea/ListShareArea')
)

//Payment Gateways
const PaymentGatewaysList = lazy(
  () =>
    import(
      '@/containers/config/GeneralAdjustment/paymentGateways/PaymentGatewaysList'
    )
)

//Modules and Services
const ListModulesServices = lazy(
  () => import('@/containers/config/GeneralAdjustment/Visual')
)

//Almacenes
const ListStocks = lazy(() => import('../containers/areas/ListStocks'))

// Cupones
const ListCoupons = lazy(
  () => import('../containers/products/coupons/ListCoupons')
)

// Ciclos económicos
const OrdersEcoCycle = lazy(
  () => import('../containers/economicCycles/OrdersEcoCycle')
)

// Categorías de clientes
const ListCustomerCategory = lazy(
  () => import('../containers/onlineClients/categories/CategoriesRegisterList')
)

// Registro de asistencia
const AttendanceRecord = lazy(
  () => import('../containers/humanResources/attendanceRecord/AttendanceRecord')
)

// Generador de salarios
const Generator = lazy(
  () => import('../containers/humanResources/salary/all/Generator')
)

// Reglas de salarios
const ListRules = lazy(
  () => import('../containers/humanResources/salary/rules/ListRules')
)
// diaries
const ListDiaries = lazy(
  () => import('../containers/humanResources/salary/daily/ListDiaries')
)

// Historial de salarios
const Historical = lazy(
  () => import('../containers/humanResources/salary/historical/Historical')
)
const HistoricalDetails = lazy(
  () =>
    import('../containers/humanResources/salary/historical/HistoricalDetails')
)

const SalaryAnalysis = lazy(
  () => import('../containers/humanResources/salary/analysis/SalaryAnalysis')
)

// Resumen de facturación
const BillingSummary = lazy(
  () => import('../containers/billing/summary/BillingSummary')
)

// Reservas
const CalendarReservation = lazy(
  () => import('../containers/reservations//calendar/CalendarReservation')
)
const AllReservations = lazy(
  () =>
    import(
      '../containers/reservations/calendar/allReservations/AllReservations'
    )
)
const ReservationSettingsMain = lazy(
  () => import('../containers/reservations/settings/ReservationSettingsMain')
)

// Reportes
const AllReports = lazy(() => import('../containers/analysis/AllReports'))

// Órdenes de tienda
const ListAllOrders = lazy(() => import('../containers/store/ListAllOrders'))

// Cartelera digital
const CartDigital = lazy(() => import('../containers/cardDigital/CartDigital'))

// Configuraciones
const RegionConfig = lazy(() => import('../containers/config/RegionConfig'))
const ConfigMain = lazy(() => import('../containers/config/ConfigMain'))
const PriceSystemConfig = lazy(
  () => import('../containers/config/PriceSystemConfig')
)
const FixedCosts = lazy(() => import('../containers/config/FixedCosts'))
const CurrenciesConfig = lazy(
  () => import('../containers/config/CurrenciesConfig')
)

const DetailStockContainer = lazy(
  () => import('@/containers/areas/stock/DetailStockContainer')
)

const ListStockDispatches = lazy(
  () => import('@/containers/areas/stock/dispatch/ListStockDispatches')
)
const ListStockMovements = lazy(
  () => import('@/containers/areas/stock/listMovements/ListStockMovements')
)
const ListStockDetailsMovements = lazy(
  () => import('@/containers/areas/stock/listMovements/StockMovements')
)

const ListStockInventories = lazy(
  () => import('@/containers/areas/stock/inventories/ListStockInventories')
)

const ListStockDetailsInventories = lazy(
  () => import('@/containers/areas/stock/inventories/StockInventories')
)

const ReportContainer = lazy(
  () => import('@/containers/areas/stock/reports/ReportContainer')
)

const BuyReceiptList = lazy(
  () => import('@/containers/areas/stock/buy/BuyReceiptList')
)
const CargoAnalisis = lazy(
  () => import('@/containers/areas/stock/buy/CargoAnalisis')
)

const ListOfOriginDestiny = lazy(
  () => import('@/containers/areas/stock/ListOfOriginDestiny')
)

//Productos
const ListProducts = lazy(() => import('@/containers/products/ListProducts'))

const ListAllProductsReadyForSale = lazy(
  () => import('@/containers/products/ListAllProductsReadyForSale')
)

const ListStockCategories = lazy(
  () => import('@/containers/products/ListStockCategories')
)
const ListResource = lazy(() => import('@/containers/products/ListResource'))

const ListSalesCategories = lazy(
  () => import('@/containers/products/ListSalesCategories')
)

//Órdenes de producción
const ListProductionOrders = lazy(
  () => import('@/containers/productionOrders/orders/ListProductionOrders')
)

//Recetario
const ListRecipes = lazy(
  () => import('@/containers/productionOrders/recipe/ListRecipes')
)

//Analisis
const ProductionAnalysis = lazy(
  () =>
    import('@/containers/productionOrders/analysis/ProductionOrdersAnalysis')
)

//Ciclos económicos
const ListEcoCyclesPage = lazy(
  () => import('@/containers/economicCycles/ListEcoCycles')
)

const DetailEcoCycleContainer = lazy(
  () => import('@/containers/economicCycles/DetailEcoCycleContainer')
)

const EconomicCyclesAnalysis = lazy(
  () => import('@/containers/economicCycles/analysis/EconomicCyclesAnalysis')
)

//Cuentas bancarias
const ListBankAccounts = lazy(
  () => import('@/containers/bankAccount/ListBankAccounts')
)

const MainBankAccount = lazy(
  () => import('@/containers/bankAccount/bankAccounts/MainBankAccount')
)

const MainBankAccountReport = lazy(
  () => import('@/containers/bankAccount/reports/MainBankAccountReport')
)

const ListBalance = lazy(
  () => import('@/containers/bankAccount/listbalance/ListBalance')
)

const BankAccountAnalysis = lazy(
  () => import('@/containers/bankAccount/analysis/BankAccountAnalysis')
)

const BankAccountFinancialState = lazy(
  () => import('@/containers/bankAccount/reports/FinancialState')
)

//Usuarios
const ListUsers = lazy(
  () => import('@/containers/humanResources/users/UsersRegisterList')
)
const UsersDetails = lazy(
  () => import('@/containers/humanResources/users/UserRegister')
)
const ListPeople = lazy(
  () => import('@/containers/humanResources/persons/ListPeople')
)
const ListCategories = lazy(
  () => import('@/containers/humanResources/categories/ListCategories')
)
const ListPost = lazy(
  () => import('@/containers/humanResources/post/ListPosts')
)
// const ListAccessRecord = lazy(() => import("../containers/humanResources/ListAccessRecord"));
// const SalaryContainer = lazy(
//   () => import('@/containers/humanResources/salary/SalaryContainer')
// )

//Clientes
const OnlineClientsContainer = lazy(
  () => import('@/containers/onlineClients/ClientRegisterList')
)

const DetailOnlineClientContainer = lazy(
  () => import('@/containers/onlineClients/ClientRegister')
)

//Proveedores
const ListSuppliers = lazy(() => import('@/containers/suppliers/ListSuppliers'))

const DetailSupplier = lazy(
  () => import('@/containers/suppliers/DetailSupplier')
)

//Facturación
const BillingRegisterList = lazy(
  () => import('@/containers/billing/register/BillingRegisterList')
)
const BillingRegister = lazy(
  () => import('@/containers/billing/register/BillingRegister')
)

const AllRegistersList = lazy(
  () => import('@/containers/billing/register/AllRegistersList')
)

// Operaciones de caja
const CashOperationsList = lazy(
  () => import('@/containers/cashOperations/CashOperationsList')
)

const CashOperationsReport = lazy(
  () => import('@/containers/cashOperationsReport/CashOperationsReport')
)

const PrepaidPaymentsList = lazy(
  () => import('@/containers/prepaidPayments/prepaidPaymentsList')
)

const ReportDetailsPage = lazy(
  () => import('@/containers/cashOperationsReport/ReportDetails')
)
// Cobros vencidos
const OverduePayments = lazy(
  () => import('@/containers/billing/overduePayments/OverduePayments')
)

// Pagos anticipados
const AllPrepaidList = lazy(
  () => import('@/containers/billing/prepaid/AllPrepaidList')
)

// Análisis
const BillingAnalysis = lazy(
  () => import('@/containers/billing/analysis/BillingAnalysis')
)

// Modulo de notificaciones
const NotificationConfig = lazy(
  () => import('@/containers/config/notifications/NotificationsConfig')
)
// const CargoScreen = lazy(
//   () => import('@/containers/logistic/cargo/CargoScreen')
// )

const ExpedientList = lazy(
  () => import('@/containers/logistic/expedients/ExpedientList')
)

// Inventario logístico
const LotesView = lazy(
  () => import('@/containers/logistic/inventory/lotes/LotesView')
)

//Tienda
// const StoreGalery = lazy(() => import('@/containers/store/StoreGalery'))

//Configurations
const MyBusiness = lazy(() => import('@/containers/config/MyBusiness'))

//Turns
const TurnPage = lazy(() => import('../containers/turns/all/TurnPage'))

const PayedRoute = () => {
  const { business } = useAppSelector((state) => state.init)
  const { allowRoles, allowModules } = useServer()
  const navigate = useNavigate()
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})
  const { businessPackages } = useBusinessPackagesQuery({
    params: { businessId: businessData?.id ?? 0 },
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  useEffect(() => {
    if (!allowRoles(['ADMIN'])) {
      if (allowRoles(['MANAGER_AREA'], true)) {
        navigate('/stocks')
      } else if (allowRoles(['MANAGER_CONTABILITY'], true)) {
        navigate('/bank_accounts')
      } else if (allowRoles(['CHIEF_PRODUCTION'], true)) {
        navigate('/orders')
      } else if (
        allowRoles(['MANAGER_SHIPPING', 'MANAGER_SHOP_ONLINE'], true)
      ) {
        navigate('/online_shop')
      } else if (
        allowRoles(
          [
            'MANAGER_ECONOMIC_CYCLE',
            'MANAGER_SHIFT',
            'MANAGER_SALES',
            'ADMIN',
            'MANAGER_SHOP_ONLINE',
          ],
          true
        )
      ) {
        navigate('/ecocycle')
      } else if (allowRoles(['ANALYSIS_REPORT'], true)) {
        navigate('/')
      } else if (
        allowRoles(['MANAGER_COST_PRICES', 'PRODUCT_PROCESATOR'], true)
      ) {
        navigate('/products/all')
      } else if (allowRoles(['MANAGER_CUSTOMERS'], true)) {
        navigate('/clients/all')
      } else if (allowRoles(['MANAGER_SUPPLIERS'], true)) {
        navigate('/suppliers')
      } else if (allowRoles(['MANAGER_HUMAN_RESOURCES'], true)) {
        // !!business?.configurationsKey.find(
        //   (itm) => itm.key === 'module_human_resources' && itm.value === 'true'
        // )
        if (
          businessPackages?.find(
            (itm) => itm.code === 'M_HUMAN_RESOURCES' && itm.isActive
          )
        )
          navigate('/human_resources/people')
        else navigate('/human_resources/users')
      } else if (allowRoles(['MANAGER_SALARY_RULES'], true)) {
        navigate('/salary/generator')
      } else if (allowRoles(['MANAGER_CURRENCIES'], true)) {
        navigate('/configurations/currencies')
      } else if (allowRoles(['MANAGER_CONFIGURATIONS'], true)) {
        navigate('/configurations/my_business')
      } else if (allowRoles(['MANAGER_BILLING'], true)) {
        navigate('/billing/registers')
      } else if (allowRoles(['MARKETING_SALES'], true)) {
        navigate('/products/sales')
      } else if (allowRoles(['BUYER'], true)) {
        navigate('/load/receipt')
      }
    }
  }, [allowRoles, businessPackages, navigate])

  const module_booking =
    business?.configurationsKey.find((itm) => itm.key === 'module_booking')
      ?.value === 'true'

  return (
    <Routes>
      <Route element={<AppContainer />} path="/">
        {/**Home */}
        {allowRoles(['ADMIN', 'ANALYSIS_REPORT']) && (
          <Route element={<HomePage />} index />
        )}
        {/**Stocks */}
        {allowRoles(['ADMIN', 'MANAGER_AREA', 'ANALYSIS_REPORT', 'BUYER']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStocks />
                </Suspense>
              }
              index={allowRoles(['MANAGER_AREA'], true)}
              path="/stocks"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <DetailStockContainer />
                </Suspense>
              }
              path="/stocks/:stockId"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStockMovements />
                </Suspense>
              }
              path="/stocks/movements"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStockDetailsMovements />
                </Suspense>
              }
              path="/stocks/movements/:stockId"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStockInventories />
                </Suspense>
              }
              path="/stocks/inventories"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStockDetailsInventories />
                </Suspense>
              }
              path="/stocks/inventories/:inventoryId"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStockDispatches />
                </Suspense>
              }
              path="/stocks/dispatches"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ReportContainer />
                </Suspense>
              }
              path="/stocks/analysis"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BuyReceiptList />
                </Suspense>
              }
              path="/load/receipt"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListOfOriginDestiny />
                </Suspense>
              }
              path="/load/origin_destiny"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CargoAnalisis />
                </Suspense>
              }
              path="/load/analisis"
            />
          </>
        )}
        {/**Products */}
        {allowRoles([
          'ADMIN',
          'PRODUCT_PROCESATOR',
          'MANAGER_COST_PRICES',
          'MANAGER_BILLING',
          'MANAGER_SALES',
          'MANAGER_SHIFT',
          'MARKETING_SALES',
          'MANAGER_AREA',
        ]) && (
          <>
            {allowRoles([
              'ADMIN',
              'PRODUCT_PROCESATOR',
              'MANAGER_COST_PRICES',
              'MANAGER_AREA',
            ]) && (
              <>
                <Route
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <ListProducts />
                    </Suspense>
                  }
                  index={allowRoles(
                    [
                      'PRODUCT_PROCESATOR',
                      'MANAGER_COST_PRICES',
                      'MANAGER_AREA',
                    ],
                    true
                  )}
                  path="/products/all"
                />
                {allowRoles([
                  'ADMIN',
                  'PRODUCT_PROCESATOR',
                  'MANAGER_COST_PRICES',
                ]) && (
                  <>
                    <Route
                      element={
                        <Suspense fallback={<Loading loading={false} />}>
                          <ListSalesCategories />
                        </Suspense>
                      }
                      path="/products/sales_categories"
                    />
                    <Route
                      element={
                        <Suspense fallback={<Loading loading={false} />}>
                          <ListStockCategories />
                        </Suspense>
                      }
                      path="/products/general_categories"
                    />
                    <Route
                      element={
                        <Suspense>
                          <ListCoupons />
                        </Suspense>
                      }
                      path="/products/cupons"
                    />
                  </>
                )}
              </>
            )}
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListAllProductsReadyForSale />
                </Suspense>
              }
              path="/products/sales"
            />

            {module_booking && (
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListResource />
                  </Suspense>
                }
                path="/products/resources"
              />
            )}
          </>
        )}
        {/**Production Orders */}
        {allowRoles([
          'ADMIN',
          'CHIEF_PRODUCTION',
          'MANAGER_PRODUCTION',
          'MANAGER_COST_PRICES',
        ]) &&
          // business?.configurationsKey.find(
          //   (itm) => itm.key === 'module_production'
          // )?.value === 'true'
          businessPackages?.find((itm) => itm.code === 'M_PRODUCTION')
            ?.isActive && (
            <>
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListProductionOrders />
                  </Suspense>
                }
                index={allowRoles(['CHIEF_PRODUCTION'], true)}
                path="/orders"
              />
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListRecipes />
                  </Suspense>
                }
                path="orders/recipes"
              />
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ProductionAnalysis />
                  </Suspense>
                }
                path="/orders/analysis"
              />
            </>
          )}
        {/**Economic Cycles */}
        {allowRoles([
          'ADMIN',
          'MANAGER_ECONOMIC_CYCLE',
          'MANAGER_SHIFT',
          'MANAGER_SALES',
          'ANALYSIS_REPORT',
          'MANAGER_BILLING',
          'MANAGER_CHIEF',
        ]) && (
          <>
            {allowRoles([
              'ADMIN',
              'MANAGER_ECONOMIC_CYCLE',
              'MANAGER_SHIFT',
              'MANAGER_SALES',
              'MANAGER_CHIEF',
            ]) && (
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListEcoCyclesPage />
                  </Suspense>
                }
                index={allowRoles(
                  [
                    'ADMIN',
                    'MANAGER_ECONOMIC_CYCLE',
                    'MANAGER_SHIFT',
                    'MANAGER_SALES',
                    'MANAGER_CHIEF',
                  ],
                  true
                )}
                path="/ecocycle"
              />
            )}
            {allowRoles([
              'ADMIN',
              'MANAGER_ECONOMIC_CYCLE',
              'MANAGER_CHIEF',
            ]) && (
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <OrdersEcoCycle />
                  </Suspense>
                }
                path="/ecocycle/accounts"
              />
            )}
            {allowRoles([
              'ADMIN',
              'ANALYSIS_REPORT',
              'MANAGER_SHOP_ONLINE',
              'MANAGER_SALES',
              'MANAGER_SHIFT',
              'MANAGER_BILLING',
              'MANAGER_CHIEF',
            ]) && (
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <EconomicCyclesAnalysis />
                  </Suspense>
                }
                path="/ecocycle/analysis"
              />
            )}
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <DetailEcoCycleContainer />
                </Suspense>
              }
              path="/ecocycle/:ecoCycleId"
            />
          </>
        )}
        {/* Bank Account */}
        {allowRoles(['MANAGER_CONTABILITY', 'ANALYSIS_REPORT']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListBankAccounts />
                </Suspense>
              }
              index={allowRoles(['MANAGER_CONTABILITY'], true)}
              path="/bank_accounts"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <MainBankAccount />
                </Suspense>
              }
              path="/bank_accounts/:bankAccountId"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <MainBankAccountReport />
                </Suspense>
              }
              path="/bank_accounts/reports"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListBalance />
                </Suspense>
              }
              path="/bank_accounts/lists"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BankAccountAnalysis />
                </Suspense>
              }
              path="/bank_accounts/analysis"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BankAccountFinancialState />
                </Suspense>
              }
              path="/bank_accounts/finance/state"
            />
          </>
        )}
        {/**Clients */}
        {allowRoles(['ADMIN', 'MANAGER_SHOP_ONLINE', 'MANAGER_CUSTOMERS']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <OnlineClientsContainer />
                </Suspense>
              }
              path="/clients/all"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListCustomerCategory />
                </Suspense>
              }
              path="/clients/categories"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <DetailOnlineClientContainer />
                </Suspense>
              }
              path="/clients/all/:clientId"
            />
          </>
        )}
        {/**Human Resourses */}
        {allowRoles(['ADMIN', 'MANAGER_HUMAN_RESOURCES']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListUsers />
                </Suspense>
              }
              index={allowRoles(['MANAGER_HUMAN_RESOURCES'], true)}
              path="/users/all"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <UsersDetails />
                </Suspense>
              }
              index={allowRoles(['MANAGER_HUMAN_RESOURCES'], true)}
              path="/users/all/:userId"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListPeople />
                </Suspense>
              }
              index={allowRoles(['MANAGER_HUMAN_RESOURCES'], true)}
              path="/human_resources/people"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListCategories />
                </Suspense>
              }
              path="/human_resources/categories"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListPost />
                </Suspense>
              }
              path="/human_resources/post"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <AttendanceRecord />
                </Suspense>
              }
              path="/human_resources/access"
            />
          </>
        )}

        {/*Salarios*/}
        {allowRoles(['ADMIN', 'MANAGER_SALARY_RULES']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <Generator />
                </Suspense>
              }
              index={allowRoles(['MANAGER_SALARY_RULES'], true)}
              path="/salary/generator"
            />

            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListRules />
                </Suspense>
              }
              path="/salary/rules"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListDiaries />
                </Suspense>
              }
              path="/salary/daily"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <Historical />
                </Suspense>
              }
              path="/salary/historical"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <HistoricalDetails />
                </Suspense>
              }
              path="/salary/historical/:historicalName/:historicalID"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <SalaryAnalysis />
                </Suspense>
              }
              path="/salary/analysis"
            />
          </>
        )}

        {/**Suppliers */}
        {business?.configurationsKey.find(
          (itm) => itm.key === 'visual_suppliers'
        )?.value === 'true' &&
          allowRoles(['ADMIN', 'MANAGER_SUPPLIERS']) && (
            <>
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListSuppliers />
                  </Suspense>
                }
                path="/suppliers"
              />
              <Route
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <DetailSupplier />
                  </Suspense>
                }
                path="/suppliers/:supplierId"
              />
            </>
          )}
        {/**Billing */}
        {
          // business?.configurationsKey.find((itm) => itm.key === 'module_billing')
          //   ?.value === 'true'
          businessPackages?.find((itm) => itm.code === 'M_BILLING')?.isActive &&
            allowRoles([
              'ADMIN',
              'MANAGER_SALES',
              'MANAGER_SHIFT',
              'MANAGER_BILLING',
              'MARKETING_SALES',
              'MANAGER_CHIEF',
            ]) && (
              <>
                <Route
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <AllRegistersList />
                    </Suspense>
                  }
                  path="/billing/registers"
                />
                <Route
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <BillingSummary />
                    </Suspense>
                  }
                  path="/billing/resume"
                />
                <Route
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <OverduePayments />
                    </Suspense>
                  }
                  path="/billing/overdue_payments"
                />
                <Route
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <AllPrepaidList />
                    </Suspense>
                  }
                  path="/billing/prepaid"
                />
                {allowRoles([
                  'ADMIN',
                  'OWNER',
                  'MANAGER_BILLING',
                  'ANALYSIS_REPORT',
                ]) && (
                  <Route
                    element={
                      <Suspense fallback={<Loading loading={false} />}>
                        <BillingAnalysis />
                      </Suspense>
                    }
                    path="/billing/analysis"
                  />
                )}
              </>
            )
        }
        {allowRoles([
          'ADMIN',
          'MANAGER_SALES',
          'MANAGER_SHIFT',
          'MANAGER_BILLING',
          'MARKETING_SALES',
          'MANAGER_SHIPPING',
          'MANAGER_CHIEF',
        ]) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BillingRegisterList />
                </Suspense>
              }
              path="/registers-beta/all"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BillingRegister />
                </Suspense>
              }
              path="/registers-beta/all/:invoiceId"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CashOperationsList />
                </Suspense>
              }
              path="/cash-operations/all"
            />
          </>
        )}
        {allowRoles(['OWNER', 'CASH_OPERATOR']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CashOperationsReport />
                </Suspense>
              }
              path="/cash-operations/report"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ReportDetailsPage />
                </Suspense>
              }
              path="/cash-operations/report/economic-cycle/:economicCycleId"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <PrepaidPaymentsList />
                </Suspense>
              }
              path="/registers/prepaid-payments"
            />
          </>
        )}
        {/**Logistic */}
        {allowModules(['M_LOGISTICS']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <ContainerList />
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/containers"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <ExpedientList />
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/files"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <ShipmentNotificationList />
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/shipment-notifications"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <CompanyList />
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/companies"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <CategoryList />
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/categories"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <VehicleList />
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/vehicles"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <PurchaseOrderList />
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/purchase-orders"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <InventoryList />
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/inventory"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <LogisticWrapper>
                    <LogisticInventoryProvider>
                      <ProductDetailsProvider>
                        <LotesView />
                      </ProductDetailsProvider>
                    </LogisticInventoryProvider>
                  </LogisticWrapper>
                </Suspense>
              }
              path="/logistic/inventory/:inventoryId/lotes"
            />
          </>
        )}

        {/**Reservations */}
        {allowRoles([
          'ADMIN',
          'OWNER',
          'MANAGER_BILLING',
          'ANALYSIS_REPORT',
        ]) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CalendarReservation />
                </Suspense>
              }
              path="/reservation/calendar"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <AllReservations />
                </Suspense>
              }
              path="/reservation/all"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ReservationSettingsMain />
                </Suspense>
              }
              path="/reservation/adjustment"
            />
          </>
        )}

        {/**Análisis */}
        {allowRoles(['ADMIN', 'ANALYSIS_REPORT', 'MANAGER_SHOP_ONLINE']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <AllReports />
                </Suspense>
              }
              path="/reports"
            />
          </>
        )}

        {/**Store */}
        {allowRoles(['ADMIN', 'MANAGER_SHOP_ONLINE', 'MANAGER_SHIPPING']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListAllOrders />
                </Suspense>
              }
              index={allowRoles(
                ['MANAGER_SHOP_ONLINE', 'MANAGER_SHIPPING'],
                true
              )}
              path="/online_shop"
            />
          </>
        )}

        {/* Cartelera digital */}
        <Route
          element={
            <Suspense fallback={<Loading loading={false} />}>
              <CartDigital />
            </Suspense>
          }
          index={allowRoles(['MANAGER_SHOP_ONLINE'], true)}
          path="/cart_digital"
        />

        {/**Configurations */}
        {allowRoles([
          'ADMIN',
          'MANAGER_COST_PRICES',
          'MANAGER_CURRENCIES',
          'MANAGER_CONFIGURATIONS',
          'MANAGER_SALES',
          'MANAGER_SHIFT',
        ]) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <MyBusiness />
                </Suspense>
              }
              path="/configurations/my_business"
            />
            {/*<Route
              path="/configurations/my_areas"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListAreas />
                </Suspense>
              }
            /> */}
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <AreaList />
                </Suspense>
              }
              path="/configurations/my_areas"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <DetailAreaContainer />
                </Suspense>
              }
              path="/configurations/my_areas/:areaId"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <>
                    <Breadcrumb
                      icon={<Cog8ToothIcon className="h-6 text-gray-500" />}
                      paths={[
                        {
                          name: 'Configuraciones',
                        },
                        {
                          name: 'Áreas compartidas',
                        },
                      ]}
                    />
                    <ListShareAreas />
                  </>
                </Suspense>
              }
              path="/configurations/shared_areas"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <RegionConfig />
                </Suspense>
              }
              path="/configurations/regions"
            />

            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ConfigMain />
                </Suspense>
              }
              path="/configurations/settings"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <PriceSystemConfig />
                </Suspense>
              }
              path="configurations/pricessystem"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <>
                    <Breadcrumb
                      icon={<Cog8ToothIcon className="h-6 text-gray-500" />}
                      paths={[
                        {
                          name: 'Configuraciones',
                        },
                        {
                          name: 'Módulos y servicios',
                        },
                      ]}
                    />
                    <ListModulesServices />
                  </>
                </Suspense>
              }
              path="configurations/modules_services"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <>
                    <Breadcrumb
                      icon={<Cog8ToothIcon className="h-6 text-gray-500" />}
                      paths={[
                        {
                          name: 'Configuraciones',
                        },
                        {
                          name: 'Pasarelas de pago',
                        },
                      ]}
                    />
                    <PaymentGatewaysList />
                  </>
                </Suspense>
              }
              path="configurations/payment_gateways"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <FixedCosts />
                </Suspense>
              }
              path="configurations/fixedcosts"
            />
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CurrenciesConfig />
                </Suspense>
              }
              path="/configurations/currencies"
            />
            {
              // business?.configurationsKey.find(
              //   (itm) => itm.key === 'module_notifications'
              // )?.value === 'true'
              businessPackages?.find((itm) => itm.code === 'M_NOTIFICATIONS')
                ?.isActive && (
                <Route
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <NotificationConfig />
                    </Suspense>
                  }
                  path="/configurations/notifications"
                />
              )
            }
          </>
        )}

        {/**Cartelera Digital */}
        {allowRoles([
          'ADMIN',
          'MANAGER_COST_PRICES',
          'MANAGER_CURRENCIES',
          'MANAGER_CONFIGURATIONS',
          'MANAGER_SALES',
          'MANAGER_SHIFT',
        ]) && (
          <Route
            element={
              <Suspense fallback={<Loading loading={false} />}>
                <CartDigital />
              </Suspense>
            }
            path="/digital_billboard"
          />
        )}

        {/**Turns */}
        {allowRoles(['ADMIN', 'MANAGER_ACCESS_POINT']) && (
          <>
            <Route
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <TurnPage />
                </Suspense>
              }
              path="/turns"
            />
          </>
        )}

        {/**Notification */}
        <Route
          element={
            <Suspense fallback={<Loading loading={false} />}>
              <NotificationConfig />
            </Suspense>
          }
          path="/notifications"
        />
      </Route>
      <Route element={<NotFoundPage />} path="*" />
    </Routes>
  )
}

export default PayedRoute
